import {
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IOption } from '@miq/shared-models';
import { SharedApiService } from '@miq/platform';
import { of, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import { limit } from './search.contants';
import { MatIcon } from '@angular/material/icon';
import { UpperCasePipe } from '@angular/common';
import { L10nTranslationModule } from 'angular-l10n';
/**
 * L2-S1/T7: moved from `src/app/shared/components/search/search.component.ts`, where it was a
 * sanctioned reports-remote seam (the remote's `add-record-action` renders it to attach a record to
 * a report).
 *
 * ONE blocker had to be removed: it injected the host's 868-line `SharedService` for a single
 * method, `searchRecords`. That method moved to `SharedApiService` in @miq/platform — the same
 * carve-out pattern the rest of the god service has been following since L2-P0.3 — and
 * `SharedService.searchRecords` now delegates to it, so host callers are untouched.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-search',
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
  imports: [FormsModule, ReactiveFormsModule, MatIcon, L10nTranslationModule, UpperCasePipe],
})
export class SearchComponent implements OnInit, OnDestroy {
  public searchControl = new FormControl<string>('');
  private readonly clearResults$ = new Subject<void>();
  // Subscribed imperatively in ngOnInit so the first valueChanges emission is
  // never missed. Binding the pipeline through `| async` inside the @if block
  // means the async pipe only subscribes after CD renders the dropdown, by
  // which point the single emission triggered by a paste has already been lost
  // (valueChanges is a hot Subject, not a BehaviorSubject) — typing appears to
  // work only because the second keystroke is the first one the async pipe
  // actually receives (CM-29995).
  public textSearch = signal<IOption[]>([]);
  // Decouples "dropdown visible" from "searchControl has value". After
  // selection we hide the dropdown but keep the typed/pasted text in the
  // input so the user can see what they searched for (CM-29995).
  public isSearching = signal(false);
  userId = input<number>();
  entity = input<string>();
  filters = input([]);
  newRecord = output<any>();
  close = output<void>();
  private readonly sharedService = inject(SharedApiService);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  ngOnInit() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        switchMap(value => {
          const trimmed = (value ?? '').trim();
          if (!trimmed) return of<IOption[]>([]);
          return this.sharedService
            .searchRecords(
              {
                entity: this.entity().toLowerCase(),
                userId: this.userId(),
                query: trimmed,
                filters: [
                  { dataType: 'text', field: [], type: 'multifield', values: trimmed },
                  ...this.filters(),
                ],
              },
              limit
            )
            .pipe(map(({ results }) => results as IOption[]));
        }),
        takeUntil(this.clearResults$)
      )
      .subscribe(results => {
        this.textSearch.set(results);
        this.isSearching.set(true);
        this.cdr.markForCheck();
      });
  }
  public addRecord(record: IOption) {
    this.newRecord.emit(record);
    this.textSearch.set([]);
    this.isSearching.set(false);
    this.close.emit();
  }
  // Dismiss the popup when the user clicks outside the search component.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Node | null;
    if (target && !this.hostEl.nativeElement.contains(target)) {
      this.close.emit();
    }
  }
  ngOnDestroy() {
    this.clearResults$.next();
    this.clearResults$.complete();
  }
}
